import React, { useState, useEffect } from "react";
import styled from "styled-components";

import api from "../../../../services/api";
import { Licensee } from "../../dtos";

export const customerPlanCache = new Map<string, string>();
const pendingPlanRequests = new Map<string, Promise<string | null>>();
export const plansMapCache = new Map<string, string>();
let plansPromise: Promise<Map<string, string>> | null = null;

export function invalidateCustomerPlanCache(licenseeId?: string) {
  if (licenseeId) {
    customerPlanCache.delete(licenseeId);
  } else {
    customerPlanCache.clear();
  }
}

export function setCustomerPlanCache(licenseeId: string, planName: string) {
  if (licenseeId) {
    customerPlanCache.set(licenseeId, planName);
  }
}

export async function fetchPlansMap(): Promise<Map<string, string>> {
  if (plansMapCache.size > 0) {
    return plansMapCache;
  }

  if (plansPromise) {
    return plansPromise;
  }

  plansPromise = (async () => {
    try {
      const response = await api.get("/plans");
      const list = Array.isArray(response.data) ? response.data : [];
      list.forEach((p: any) => {
        if (p.id && p.plan_name) {
          plansMapCache.set(String(p.id), p.plan_name);
        }
      });
      return plansMapCache;
    } catch {
      return plansMapCache;
    }
  })();

  return plansPromise;
}

const isCurrentSig = (s: any) =>
  Boolean(s) &&
  (!s.end_date || new Date(s.end_date).getTime() >= Date.now()) &&
  s.status !== "cancelled" &&
  s.status !== "canceled";

const sortSignaturesDesc = (list: any[]) =>
  [...list].sort((a, b) => {
    const tA = new Date(a?.created_at || a?.start_date || 0).getTime();
    const tB = new Date(b?.created_at || b?.start_date || 0).getTime();
    return (isNaN(tB) ? 0 : tB) - (isNaN(tA) ? 0 : tA);
  });

export async function fetchLicenseePlan(licenseeId: string): Promise<string | null> {
  if (!licenseeId) return null;

  if (customerPlanCache.has(licenseeId)) {
    return customerPlanCache.get(licenseeId) || null;
  }

  if (pendingPlanRequests.has(licenseeId)) {
    return pendingPlanRequests.get(licenseeId)!;
  }

  const promise = (async () => {
    try {
      const plansMap = await fetchPlansMap();

      let planName = "";
      try {
        const response = await api.get(`/signatures/get-signature/${licenseeId}`);
        const sig = response.data;
        if (sig && isCurrentSig(sig)) {
          planName =
            sig.plan?.plan_name ||
            sig.plans?.plan_name ||
            (sig.plan_id ? plansMap.get(String(sig.plan_id)) || "" : "");
        }
      } catch {
        try {
          const res = await api.get(`/signatures/${licenseeId}`);
          const sig = Array.isArray(res.data)
            ? sortSignaturesDesc(res.data).find(isCurrentSig) || res.data[0]
            : isCurrentSig(res.data)
            ? res.data
            : null;
          if (sig) {
            planName =
              sig.plan?.plan_name ||
              sig.plans?.plan_name ||
              (sig.plan_id ? plansMap.get(String(sig.plan_id)) || "" : "");
          }
        } catch {
          // Sem assinatura
        }
      }

      const finalName = planName || "Sem plano";
      customerPlanCache.set(licenseeId, finalName);
      return finalName;
    } catch {
      customerPlanCache.set(licenseeId, "Sem plano");
      return "Sem plano";
    } finally {
      pendingPlanRequests.delete(licenseeId);
    }
  })();

  pendingPlanRequests.set(licenseeId, promise);
  return promise;
}

const PlanBadge = styled.span<{ $hasPlan?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${(props) =>
    props.$hasPlan ? "rgba(152, 53, 209, 0.08)" : "rgba(138, 140, 144, 0.12)"};
  color: ${(props) =>
    props.$hasPlan ? "var(--fixit)" : "var(--dark-gray)"};
  border: 1px solid
    ${(props) =>
      props.$hasPlan ? "rgba(152, 53, 209, 0.18)" : "rgba(138, 140, 144, 0.24)"};
`;

interface CustomerPlanBadgeProps {
  customer: Licensee;
}

export function CustomerPlanBadge({ customer }: CustomerPlanBadgeProps) {
  const rawSig = customer.signature ?? (customer as any).signatures;
  const hasPreloadedSignatures =
    rawSig !== undefined || (customer as any).active_signature !== undefined;
  const sigList = sortSignaturesDesc(Array.isArray(rawSig) ? rawSig : rawSig ? [rawSig] : []);
  const preloadedActiveSig = (customer as any).active_signature;
  const activeSig =
    (isCurrentSig(preloadedActiveSig) ? preloadedActiveSig : undefined) ||
    sigList.find(isCurrentSig) ||
    sigList.find((s: any) => s.is_activated && (!s.end_date || new Date(s.end_date).getTime() >= Date.now())) ||
    sigList[0];

  const initialDirectPlan =
    activeSig?.plan?.plan_name ||
    activeSig?.plans?.plan_name ||
    (activeSig?.plan_id ? plansMapCache.get(String(activeSig.plan_id)) : "") ||
    "";

  const preloadedNoPlan =
    hasPreloadedSignatures && !initialDirectPlan && !activeSig?.plan_id;

  const cached = customerPlanCache.get(customer.id);
  const initialPlan =
    initialDirectPlan || cached || (preloadedNoPlan ? "Sem plano" : "");

  const [planName, setPlanName] = useState<string>(initialPlan);

  useEffect(() => {
    let isMounted = true;

    if (initialDirectPlan) {
      customerPlanCache.set(customer.id, initialDirectPlan);
      setPlanName(initialDirectPlan);
      return;
    }

    if (preloadedNoPlan) {
      customerPlanCache.set(customer.id, "Sem plano");
      setPlanName("Sem plano");
      return;
    }

    if (customerPlanCache.has(customer.id)) {
      setPlanName(customerPlanCache.get(customer.id) || "Sem plano");
      return;
    }

    if (hasPreloadedSignatures && activeSig?.plan_id) {
      fetchPlansMap().then((plansMap) => {
        if (!isMounted) return;
        const resolved =
          plansMap.get(String(activeSig.plan_id)) || "Sem plano";
        customerPlanCache.set(customer.id, resolved);
        setPlanName(resolved);
      });
      return () => {
        isMounted = false;
      };
    }

    fetchLicenseePlan(customer.id).then((resolved) => {
      if (isMounted && resolved) {
        setPlanName(resolved);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    customer.id,
    initialDirectPlan,
    preloadedNoPlan,
    hasPreloadedSignatures,
    activeSig?.plan_id,
  ]);

  const hasPlan = Boolean(planName && planName !== "Sem plano" && planName !== "-");

  return (
    <PlanBadge $hasPlan={hasPlan}>
      {planName || "-"}
    </PlanBadge>
  );
}
